import { useState } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { MAX_CHASE_SEQUENCES, nextChaseAt, topupChaseNoAnswer, topupChasePays } from '@/domain/topupChaseFlow';
import TopupMovePanel from '@/components/queue/TopupMovePanel';
import { simTimeShort, simDateShort } from '@/utils/format';

/**
 * The missed top-up chase outcomes (B8 / 75nf, diagram 05): Paid · Move date ·
 * No answer. Shared by the queue card and the work modal, so "Chase" from an
 * alert lands on the same buttons. Sequences run about a day apart: "No answer"
 * is greyed until the next one is due, and the booking stays held until the
 * 3rd sequence goes unanswered. "Move date" opens B4's move panel (75my): the
 * new date is held until a >= $50 partial is paid — nothing is recorded as paid
 * here.
 */
export default function TopupChaseBar({
  planId,
  customerId,
  testid,
  buttonTestid = (key) => `${testid}-${key}`,
}: {
  planId: string;
  customerId: string;
  testid: string;
  buttonTestid?: (key: 'paid' | 'move' | 'no-answer') => string;
}) {
  const plan = useBrainStore((s) => s.topupPlans[planId]);
  const booking = useBrainStore((s) => (plan?.bookingId ? s.bookings[plan.bookingId] : undefined));
  const clockIso = useSimulationStore((s) => s.clockIso);
  const [moving, setMoving] = useState(false);
  if (!plan) return null;
  const attempts = plan.chaseAttempts ?? 0;
  const next = nextChaseAt(plan, clockIso);
  const nextLabel = next
    ? `${simDateShort(next) === simDateShort(clockIso) ? '' : `${simDateShort(next)} `}${simTimeShort(next)}`
    : null;

  const onMove = () => setMoving((cur) => !cur);

  // bgg4 #7: say what is true of the booking, not a fixed "still held".
  const bookingState =
    plan.status === 'cancelled' || booking?.status === 'cancelled'
      ? 'booking cancelled'
      : plan.status === 'completed'
        ? 'plan paid off'
        : booking?.status === 'superseded'
          ? 'booking moved'
          : booking?.status === 'no-show'
            ? 'no-show'
            : booking?.status === 'completed'
              ? 'session done'
              : 'booking still held';

  const options = [
    { key: 'paid', label: 'Paid', tone: 'mint', onClick: () => topupChasePays(planId), disabled: false },
    { key: 'move', label: 'Move date', tone: 'cream', onClick: onMove, disabled: false },
    { key: 'no-answer', label: 'No answer', tone: 'blood', onClick: () => topupChaseNoAnswer(planId), disabled: Boolean(next) },
  ] as const;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <span className="font-inter text-[10px] uppercase tracking-widest text-gold-dim mr-1">Chase</span>
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          disabled={opt.disabled}
          onClick={(e) => {
            e.stopPropagation();
            opt.onClick();
          }}
          data-testid={buttonTestid(opt.key)}
          className={clsx(
            'rounded-md border px-2 py-1 text-[11px] uppercase tracking-widest font-inter transition disabled:opacity-40 disabled:cursor-not-allowed',
            opt.tone === 'mint' && 'border-mint/40 text-mint hover:bg-mint/10',
            opt.tone === 'blood' && 'border-blood/40 text-blood hover:bg-blood/10',
            opt.tone === 'cream' && 'border-gold-dim/40 text-cream-soft hover:bg-cream-soft/10',
          )}
        >
          {opt.label}
        </button>
      ))}
      <span className="font-serif text-[11px] text-cream-faint" data-testid={`${testid}-status`}>
        {attempts}/{MAX_CHASE_SEQUENCES} sequences · {bookingState}
        {nextLabel ? ` · next chase ${nextLabel}` : ''}
      </span>
      {moving ? (
        <div className="basis-full" onClick={(e) => e.stopPropagation()}>
          <TopupMovePanel planId={planId} customerId={customerId} onDone={() => setMoving(false)} />
        </div>
      ) : null}
    </div>
  );
}
